import React from "react";
import { Archive, ChevronRight } from "lucide-react";
import AppButton from "./AppButton.jsx";

const ROW_CLS =
  "group flex w-full items-center gap-2.5 px-3 py-1.5 sm:py-2 text-left transition-colors cursor-pointer hover:bg-white/[0.05] active:bg-white/[0.07] focus:outline-none focus-visible:bg-white/[0.06] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400/25";

/** Einspaltige Liste mit Trennlinien — ruhiger als Karten-Grid. */
export function EntityLinkList({ children, className = "", maxHeightClassName = "", scrollable = false }) {
  const scrollCls =
    scrollable || maxHeightClassName
      ? maxHeightClassName || "max-h-[min(52vh,22rem)] overflow-y-auto overscroll-contain custom-scrollbar"
      : "";
  return (
    <div
      className={[
        "rounded-lg border border-default bg-white/[0.02] overflow-hidden divide-y divide-white/10",
        scrollCls,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>
  );
}

/** @deprecated Alias — bitte EntityLinkList verwenden */
export const EntityLinkCardGrid = EntityLinkList;

export const entityLinkArchiviertChip = (
  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400/80 border border-amber-400/20 whitespace-nowrap">
    Archiviert
  </span>
);

/** Aufklappbarer Archiv-Abschnitt unter EntityLinkList */
export function EntityLinkArchivedToggle({ label, persistKey, children }) {
  const [open, setOpen] = React.useState(() => {
    if (!persistKey) return false;
    try {
      const v = localStorage.getItem(persistKey);
      return v ? JSON.parse(v) : false;
    } catch {
      return false;
    }
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (!persistKey) return;
    try {
      localStorage.setItem(persistKey, JSON.stringify(next));
    } catch {}
  };
  return (
    <div className="mt-2 pt-2 border-t border-default">
      <AppButton
        type="button"
        variant="ghost"
        onClick={toggle}
        className="w-full !justify-start text-left py-1 text-[11px] font-normal h-auto min-h-9 transition text-blue-200/50 hover:text-blue-200/80"
      >
        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-90" : ""}`} />
        <Archive className="w-3.5 h-3.5" />
        {label}
      </AppButton>
      {open ? <div className="mt-2">{children}</div> : null}
    </div>
  );
}

export default function EntityLinkCard({
  title,
  subtitle,
  meta,
  onClick,
  badge,
  trailing,
  muted = false,
  disabled = false,
  ariaLabel,
}) {
  const secondary = subtitle ?? meta;
  const end = trailing ?? badge;
  const interactive = typeof onClick === "function" && !disabled;

  const handleKeyDown = (e) => {
    if (!interactive) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onClick : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
      aria-disabled={disabled || undefined}
      aria-label={ariaLabel || (typeof title === "string" ? title : undefined)}
      className={`${ROW_CLS} ${muted || disabled ? "opacity-75" : ""} ${
        disabled ? "!cursor-not-allowed hover:!bg-transparent active:!bg-transparent" : ""
      }`}
      title={disabled ? "Nicht zugewiesen – kein Zugriff" : undefined}
    >
      <div className="min-w-0 flex-1">
        <div
          className={`text-sm font-medium leading-snug truncate ${
            muted || disabled ? "text-blue-100/60" : "text-blue-100/90"
          }`}
        >
          {title}
        </div>
        {secondary ? (
          <div className="text-xs text-blue-200/55 truncate mt-0.5 tabular-nums">{secondary}</div>
        ) : null}
      </div>
      {end ? (
        <div className="shrink-0 self-center max-w-[9.5rem] flex justify-end [&_span]:max-w-full [&_span]:truncate">
          {end}
        </div>
      ) : null}
    </div>
  );
}
